import { CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import * as React from "react";
import { BottomSheet } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";

interface DrawerContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DrawerContext = React.createContext<DrawerContextType | undefined>(
  undefined,
);

const useDrawer = () => {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a Drawer");
  }
  return context;
};

interface DrawerProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const Drawer = ({ children, open = false, onOpenChange }: DrawerProps) => {
  const [internalOpen, setInternalOpen] = React.useState(open);

  const isControlled = onOpenChange !== undefined;
  const openState = isControlled ? open : internalOpen;
  const setOpenState = isControlled ? onOpenChange : setInternalOpen;

  React.useEffect(() => {
    if (!isControlled) {
      setInternalOpen(open);
    }
  }, [open, isControlled]);

  return (
    <DrawerContext.Provider
      value={{ open: openState, onOpenChange: setOpenState }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

interface DrawerTriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
}

const DrawerTrigger = React.forwardRef<HTMLElement, DrawerTriggerProps>(
  ({ asChild = false, children, onClick, disabled, ...props }, ref) => {
    const { onOpenChange } = useDrawer();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      onOpenChange(true);
      onClick?.(event);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{
          onClick?: (event: React.MouseEvent<HTMLElement>) => void;
        }>,
        {
          ...props,
          onClick: handleClick,
        },
      );
    }

    return (
      <button
        disabled={disabled}
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  },
);
DrawerTrigger.displayName = "DrawerTrigger";

interface DrawerContentProps {
  className?: string;
  children: React.ReactNode;
  snapPoints?: ({ maxHeight }: { maxHeight: number }) => number[];
  defaultSnap?: number;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  blocking?: boolean;
  scrollLocking?: boolean;
  onDismiss?: () => void;
}

const DrawerContent = React.forwardRef<HTMLDivElement, DrawerContentProps>(
  (
    {
      className,
      children,
      snapPoints,
      defaultSnap,
      header,
      footer,
      blocking = false,
      scrollLocking = true,
      onDismiss,
      ...props
    },
    ref,
  ) => {
    const { open, onOpenChange } = useDrawer();

    const handleDismiss = () => {
      onOpenChange(false);
      onDismiss?.();
    };

    const handleBackgroundClick = (event: React.MouseEvent) => {
      // Only close if clicking on the background overlay itself
      if (event.target === event.currentTarget) {
        handleDismiss();
      }
    };

    return (
      <>
        <div
          className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ease-in-out"
          onClick={handleBackgroundClick}
          style={{
            opacity: open ? 1 : 0,
            pointerEvents: open ? "auto" : "none",
          }}
        />

        <BottomSheet
          open={open}
          onDismiss={handleDismiss}
          snapPoints={snapPoints}
          defaultSnap={defaultSnap}
          blocking={blocking}
          scrollLocking={scrollLocking}
          expandOnContentDrag
          header={header}
          footer={footer}
          className={cn(
            "react-spring-bottom-sheet z-50 transition-all duration-300 ease-in-out",
            className,
          )}
          {...props}
        >
          <div ref={ref} className="flex flex-col ">
            {children}
          </div>
        </BottomSheet>
      </>
    );
  },
);
DrawerContent.displayName = "DrawerContent";

const DrawerTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <CardTitle
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

const DrawerHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <CardHeader ref={ref} className={cn("pb-4", className)} {...props} />
));
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <CardFooter ref={ref} className={cn("pt-4", className)} {...props} />
));
DrawerFooter.displayName = "DrawerFooter";

export {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
  DrawerTitle,
};
